<template>
    <div class="CriterionCurrencyInput">
        <FormElement :label="hideTitle ? null : form.schema.title">
            <template v-if="form.schema.description" #labelHint>
                <div
                    v-tooltip="form.schema.description"
                    class="CriterionEnumInput__hintIcon"
                >
                    <FontAwesomeIcon :icon="faQuestionCircle" />
                </div>
            </template>
            <FormInput
                :invalid="showValidationErrors && (showError || accessed) && hasError"
                force-error-slot
                force-action-slot
            >
                <currency-input
                    class="CurrencyInput"
                    :value="value"
                    :precision="0"
                    :disabled="rbacDisabled"
                    value-as-integer
                    currency="usd"
                    locale="en-US"
                    @input="onInput"
                    @blur="touch"
                    @focus="onFocus"
                />
                <template v-if="showValidationErrors && (showError || accessed) && error" #errorMessage>
                    {{ error }}
                </template>
                <template #actionButtons>
                    <LockInputIcon v-if="rbacDisabled" />
                    <NewButton
                        v-else-if="deletable"
                        variant="flat"
                        size="icon-sm"
                        color="secondary"
                        class="JsonSchemaForm__deleteIcon"
                        @click="$emit('delete')"
                    >
                        <slot name="deleteIcon">
                            <EidIcon :icon-src="faXmark" alt="delete" />
                        </slot>
                    </NewButton>
                </template>
            </FormInput>
        </FormElement>
    </div>
</template>

<script lang="ts">
    import { Component } from '@evidentid/vue-property-decorator';

    import { faQuestionCircle } from '@fortawesome/free-regular-svg-icons';
    import { faXmark } from '@fortawesome/pro-light-svg-icons';
    import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

    // PRODUCT-26110 Task 8b Step 5b: vue-currency-input's own component/directive is
    // Vue-2-only and dropped outright in v3; this shared dashboard-commons wrapper
    // (built on the v3 `useCurrencyInput` composable) is a drop-in replacement with the
    // same value/currency/locale/precision/valueAsInteger/disabled props and
    // input/blur/focus events -- see its own file for the composable migration detail.
    import CurrencyInput from '@evidentid/dashboard-commons/components/CurrencyInput/CurrencyInput.vue';
    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import { FormElement, FormInput } from '@evidentid/dashboard-commons/components/Form';
    import LockInputIcon from '@evidentid/dashboard-commons/components/LockInputIcon/LockInputIcon.vue';
    import { JsonFormBase } from '@evidentid/json-schema/interfaces/JsonForm';
    import { JsonSchemaInteger, JsonSchemaNumber } from '@evidentid/json-schema/interfaces/JsonSchema';

    import NewButton from '@/components/new-button/NewButton.vue';
    import AbstractCriterionInput
        from '@/modules/decisioning-criteria/components/CriterionInputComponents/AbstractCriterionInput';

    type JsonFormNumeric = JsonFormBase<JsonSchemaNumber> | JsonFormBase<JsonSchemaInteger>;

    @Component({
        emits: [ 'delete', 'input' ],
        components: {
            EidIcon,
            FormInput,
            CurrencyInput,
            FontAwesomeIcon,
            FormElement,
            LockInputIcon,
            NewButton,
        },
    })
    export default class CriterionCurrencyInput extends AbstractCriterionInput<JsonFormNumeric, number> {
        private faXmark = faXmark;
        private faQuestionCircle = faQuestionCircle;

        // A required currency field is in error when it has no value. Zero is a value: the shared
        // CurrencyInput reports an empty cell as null, not as 0, so treating 0 as empty here only
        // put a red "required" on an amount the form otherwise accepts and saves.
        protected override get error(): string | null {
            if (this.required && this.value == null) {
                return this.translate('error.required');
            }
            if (this.empty) {
                return null;
            }
            return this.valid ? null : this.getCustomError() || this.translate('error.unknown');
        }

        private onInput(amount: number): void {
            this.$emit('input', amount);
        }
    }
</script>
